import React, { Fragment, useEffect, useRef, useState } from 'react'
import Rectangle from 'blocks.simple/rectangle/rectangle'
import Checkbox from 'blocks.simple/checkbox/checkbox'
import Icon from 'blocks.simple/icon/icon'
import Animation from 'blocks.simple/animation/animation'
import DndDraggable from 'blocks.app/dnd/_draggable/dnd_draggable'
import Tippy from 'blocks.simple/tippy/tippy'
import DotTooltip from 'blocks.simple/dotTooltip/dotTooltip'
import { cn } from 'ethcss'
import styles from './catalog__item_displays.jcss'
import grid from 'blocks.simple/grid/grid.jcss'
import item from 'blocks.simple/item/item.jcss'
import { text as textStyles } from 'theme'
import translate from 'core/translate'
import displaysHelpers from 'core/helpers/_digitalSignages'
import helpers from 'core/helpers'
import helpersCore from 'core/helpers'
import { indents } from 'blocks.app/config'
import { ProgressBar } from 'atoms/ProgressBar'
import { Status } from 'molecules/Status'
import { history } from 'features/routes'
import { TMarkerStatusType } from '../../../../atoms/Marker/Marker-types'
import store from 'core/store'

export type Item = {
    currentlyPlaying: {
        entityObject: {
            title: string
        }
    }[]
    cover: string[]
    activeQ?: boolean
    descendantStatusCount: {
        status: string
        count: number
    }[]
    __view: { selected: boolean }
    downloadPercentage: { title: string; percentage: number }[]
    identificatedItem: boolean
    name: string
    type: string
    status: string
    registrationCode: string
    hasBroadcast: boolean
    orientation: number
    modelName: string
    platform: string
    id: number
}

interface IItemDisplaysProps {
    item: Item
    deviceBlockStyle: string
    active: boolean
    onDoubleClickObject: (data: Item) => void
    onDoubleClick: (value: boolean) => void
    onSelectInfo: (e: React.MouseEvent) => void
    onSelectFolder: (item: Item) => void
    onDragStart: () => void
    onDragEnd: () => void
    drag: boolean
    multipleSelect: boolean
    onSelect: (data: { selected: boolean; item: Item }) => void
    getDragData: (data: IItemDisplaysProps) => object
    onSelectProgress: () => void
    identificatedItem: boolean
    containerWidth: number
}

const Progress = ({
    list,
    onClick,
    advanced,
}: {
    list: { title: string; percentage: number }[]
    onClick: () => void
    advanced?: boolean
}) => {
    if (list && list.length > 0) {
        return (
            <div className={cn(styles.loading, advanced ? grid.w80 : grid.fullWidth)} onClick={onClick}>
                <div className={styles.progress}>
                    <ProgressBar value={list[0].percentage} strokeWidth={17} />
                </div>
                <div className={cn(grid.row, grid.noWrap, grid.fullWidth, item.ellipsisPure)}>
                    <div className={item.ellipsisPure}>
                        {list[0].title}
                        <span>&nbsp;</span>
                    </div>
                    <div>{Math.round(list[0].percentage)}%</div>
                </div>
            </div>
        )
    } else {
        return null
    }
}

const OrientationIcon = (p_: { orientation: any }) => {
    const orientation = displaysHelpers.getOrientation(p_.orientation)

    if (!orientation) {
        return null
    }

    function getRotate() {
        if (!orientation) return ''

        switch (orientation.id) {
            case 0:
            case 180:
                return 'right'
            case 90:
            case 270:
                return 'left'
            default:
                return ''
        }
    }

    return (
        <Icon
            containerClass={styles.orientationIcon}
            color="white"
            size={20}
            type="horizontal_pos"
            rotate={getRotate()}
            tooltip={{
                title: orientation.fullName,
            }}
        />
    )
}

const ItemDisplays = (p_: IItemDisplaysProps) => {
    const [isHovered, setIsHovered] = useState(false)
    const [dragData, setDragData] = useState<object | null>(null)
    const tooltipRef = useRef<any>(null)
    const { user } = store.getState()
    const deviceTileCount = user.data?.settings.deviceTileCount || 4
    const minizedTileView = [6, 8].includes(deviceTileCount)
    const blockRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setDragData(p_.getDragData(p_))
    }, [p_])

    const disableToolTip = () => {
        tooltipRef.current = true
    }
    const enableToolTip = () => {
        tooltipRef.current = false
    }
    const getDeviceCover = (isSimple: boolean, cover: string | null) => {
        if (isSimple && cover) {
            return {
                backgroundImage: `url(${cover})`,
            }
        }

        if (!isSimple && p_.item.cover[0]) {
            const advancedCover = p_.item.cover[0]

            return {
                backgroundImage: `url(${advancedCover})`,
            }
        }
    }
    const renderGroupStatus = () => {
        if (p_.item && p_.item.descendantStatusCount) {
            return (
                <>
                    <div className={styles.statusesRow}>
                        {renderStatus('online')}
                        {renderStatus('offline')}
                        {renderStatus('error')}
                    </div>
                    <div className={styles.statusesRow}>
                        {renderStatus('online_display_off')}
                        {renderStatus('sleep')}
                        {renderStatus('updating')}
                    </div>
                </>
            )
        }
        return null
    }

    const renderStatus = (status: TMarkerStatusType) => {
        const { descendantStatusCount } = p_.item
        if (descendantStatusCount) {
            const statusInfo = descendantStatusCount.find((item: { status: string }) => item.status === status)

            if (!statusInfo) return null

            return (
                <Status tileCount={deviceTileCount} disable={statusInfo.count === 0} status={status}>
                    <div>{statusInfo.count}</div>
                </Status>
            )
        }

        return null
    }

    const getInfoLineStyle = () => {
        const infoLineStyles: Record<number, string> = {
            6: styles.infoLine6,
            8: styles.infoLine8,
        }
        return infoLineStyles[deviceTileCount]
    }

    const getStatusPanelStyle = () => {
        const statusPanelStyles: Record<number, string> = {
            6: styles.statusPanel6,
            8: styles.statusPanel8,
        }
        return statusPanelStyles[deviceTileCount]
    }

    let cover = null
    const active = p_.active || p_.item.__view.selected
    const currentTileView = p_.deviceBlockStyle
    const isSimple = currentTileView === 'simple'
    const isAdvanced = currentTileView === 'advanced'
    const isLightweight = !p_.item.status
    const showNameOverlay = isSimple || isLightweight
    const downloads = p_.item.downloadPercentage
    const identificatedItem = p_.identificatedItem

    if (active) {
        cover = p_.item.cover[0]
    }

    const zoomValue = () => {
        if (!blockRef.current) return 1

        const initialWidth = deviceTileCount === 8 ? 159 : deviceTileCount === 6 ? 212.5 : 321

        return blockRef.current.offsetWidth / initialWidth
    }

    let name = helpersCore.getTranslatedIfMain(p_.item.name)
    const iptvChannelName = displaysHelpers.getChannelName(p_.item.currentlyPlaying)

    return (
        <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={function (e) {
                p_.onSelectInfo(e)

                if (helpers.isDblTouchTap(e)) {
                    if (p_.item.type !== 'group') {
                        p_.onDoubleClickObject(p_.item)
                        p_.onDoubleClick(true)

                        e.preventDefault()
                        return
                    }

                    p_.onSelectFolder(p_.item)
                }
            }}
            onContextMenu={(e) => {
                if (helpers.isMobileDevice()) {
                    e.preventDefault()
                }
            }}
        >
            {p_.item.type !== 'group' && (
                <Rectangle
                    width={16}
                    height={9}
                    className={cn(
                        `test-digitalSignage${p_.item.platform === null ? '-virtual' : ''}-${p_.item.id}`,
                        styles.displaysWrapper,
                        isSimple && p_.item.status && styles[p_.item.status]
                    )}
                    onMouseDown={disableToolTip}
                    onMouseUp={enableToolTip}
                >
                    <DndDraggable
                        data={dragData}
                        dragEnabled={p_.drag}
                        className={cn(
                            styles.content,
                            isSimple ? p_.item.status && styles[p_.item.status] : styles.preview,
                            item.cover
                        )}
                        onDragStart={p_.onDragStart}
                        onDragEnd={p_.onDragEnd}
                        style={getDeviceCover(isSimple, cover)}
                    >
                        {iptvChannelName && (
                            <div className={styles.iptvNowPlaying}>
                                <DotTooltip tooltip={{ html: translate('iptvNowPlaying') + ': ' + iptvChannelName }}>
                                    <div className={item.ellipsis}>
                                        {translate('iptvNowPlaying') + ': ' + iptvChannelName}
                                    </div>
                                </DotTooltip>
                            </div>
                        )}
                        {p_.item.activeQ === false && (
                            <div className={styles.blocked}>
                                <img
                                    className={styles.blockedImg}
                                    src={'images/displays/license_is_blocked.svg'}
                                    alt={'license is blocked'}
                                />
                            </div>
                        )}
                        {p_.multipleSelect && (
                            <div
                                className={styles.checkWrapper}
                                onClick={(e) => e.stopPropagation()}
                                style={p_.item.__view.selected ? { opacity: 1 } : {}}
                            >
                                <Checkbox
                                    mod={'circleDis'}
                                    onClick={(selected: boolean) => {
                                        p_.onSelect({ selected, item: p_.item })
                                    }}
                                    checked={p_.item.__view.selected}
                                    className={'test-device-chbx'}
                                />
                            </div>
                        )}
                        {showNameOverlay && (
                            <div className={cn(styles.simpleWrapper, { [styles.blackLayer]: active || isLightweight })}>
                                <div className={cn(textStyles.center, textStyles.white, grid.w90, styles.displayText)}>
                                    {!isLightweight &&
                                        helpers.isAvailable({ key: 'broadcasts', action: 'read' }) &&
                                        !p_.item.hasBroadcast && (
                                            <div className={textStyles.mini}>{translate('noBroadcast')}</div>
                                        )}
                                    {!tooltipRef.current ? (
                                        <DotTooltip tooltip={{ html: name }}>
                                            <div
                                                className={cn(
                                                    !minizedTileView ? grid.p_micro : '',
                                                    textStyles.normalPlus,
                                                    item.ellipsis,
                                                    styles.displayText
                                                )}
                                            >
                                                {name}
                                            </div>
                                        </DotTooltip>
                                    ) : (
                                        <div
                                            className={cn(
                                                !minizedTileView ? grid.p_micro : '',
                                                textStyles.normalPlus,
                                                item.ellipsis,
                                                styles.displayText
                                            )}
                                        >
                                            {name}
                                        </div>
                                    )}
                                    {p_.item.registrationCode && (
                                        <div
                                            style={{ fontSize: minizedTileView ? 12 : 16 }}
                                            className={styles.registrationCodeSimple}
                                        >
                                            {p_.item.registrationCode}
                                        </div>
                                    )}
                                </div>
                                <div
                                    style={{ bottom: indents.nano, left: indents.nano }}
                                    className={cn(item.abs, grid.w90)}
                                >
                                    <Progress list={downloads} onClick={p_.onSelectProgress} />
                                </div>
                            </div>
                        )}
                        {isAdvanced && !isLightweight && (
                            <React.Fragment>
                                {p_.item.registrationCode && (
                                    <div className={styles.registrationCode}>{p_.item.registrationCode}</div>
                                )}
                                <div className={cn(grid.rowCenter, grid.full, { [styles.blackLayer]: active })} />
                            </React.Fragment>
                        )}
                    </DndDraggable>
                    {isAdvanced && !isLightweight && (
                        <div className={cn(styles.infoLine, getInfoLineStyle(), styles[p_.item.status])}>
                            {downloads && downloads.length > 0 ? (
                                <div className={cn(grid.row, grid.normalCenter, grid.fullWidth, grid.noWrap)}>
                                    <OrientationIcon orientation={p_.item.orientation} />
                                    <Progress list={downloads} advanced={isAdvanced} onClick={p_.onSelectProgress} />
                                </div>
                            ) : (
                                <div className={cn(styles.infoLine, getInfoLineStyle())}>
                                    <div className={cn(grid.col, item.ellipsisWrapper)}>
                                        <div
                                            style={{ minHeight: 24 }}
                                            className={cn(item.ellipsis, grid.fullWidth, styles.displayText)}
                                        >
                                            {name}
                                        </div>
                                        <div className={cn(item.ellipsis, textStyles.up)}>
                                            {p_.item.modelName !== 'undef' ? p_.item.modelName : ''}
                                        </div>
                                    </div>
                                    {!isHovered && identificatedItem ? (
                                        <Fragment>
                                            <div className={styles.pseudoIcon}></div>
                                            <div className={styles.infoIcon}>
                                                <Icon type="identification" size={18} color={'white'} />
                                            </div>
                                        </Fragment>
                                    ) : null}
                                    {!isHovered && !identificatedItem && p_.item.status === 'online_display_off' ? (
                                        <div className={cn(grid.pb_micro)}>{translate('displayOff')}</div>
                                    ) : null}
                                    <div className={cn(grid.col, grid.colCenter)}>
                                        <Animation>
                                            {isHovered ? (
                                                <div
                                                    className={cn(
                                                        p_.item.status === 'online_display_off' ? grid.col : grid.row,
                                                        p_.item.status === 'online_display_off' && grid.normalCenter,
                                                        grid.noWrap,
                                                        grid.justify
                                                    )}
                                                >
                                                    {p_.item.status === 'online_display_off' && (
                                                        <div className={cn(grid.pb_micro)}>
                                                            {translate('displayOff')}
                                                        </div>
                                                    )}
                                                    <div className={cn(grid.row, grid.justify)}>
                                                        <div
                                                            className={cn(grid.row, grid.normalCenter, grid.noWrap)}
                                                            style={{ gap: 8 }}
                                                        >
                                                            {helpers.isAvailable({
                                                                key: 'broadcasts',
                                                                action: 'read',
                                                            }) &&
                                                                !p_.item.hasBroadcast && (
                                                                    <Icon
                                                                        color={'white'}
                                                                        size={20}
                                                                        type={'no_display'}
                                                                        containerClass={cn(grid.mr_mini)}
                                                                        tooltip={{
                                                                            title: translate('noBroadcast'),
                                                                        }}
                                                                    />
                                                                )}
                                                            <OrientationIcon orientation={p_.item.orientation} />
                                                            {identificatedItem ? (
                                                                <Icon type="identification" size={18} color={'white'} />
                                                            ) : null}
                                                            {displaysHelpers.availablePlatforms(p_.item.platform) && (
                                                                <Tippy
                                                                    title={p_.item.platform}
                                                                    className={cn(grid.ml_mini)}
                                                                >
                                                                    <img
                                                                        alt={`${p_.item.platform}_logo`}
                                                                        width="100%"
                                                                        height="auto"
                                                                        src={`images/displays/platforms/small/${displaysHelpers.getPlatformName(
                                                                            p_.item.platform
                                                                        )}.png`}
                                                                    />
                                                                </Tippy>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </Animation>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Rectangle>
            )}
            {p_.item.type === 'group' && (
                <Rectangle
                    onMouseDown={disableToolTip}
                    onMouseUp={enableToolTip}
                    width={16}
                    height={9}
                    className={styles.groupWrapper}
                >
                    <DndDraggable
                        className={cn('digitalSignageGroup', styles.group)}
                        data={dragData}
                        dragEnabled={p_.drag}
                        style={cover ? { backgroundImage: `url(${cover})` } : {}}
                        onDragStart={p_.onDragStart}
                        onDragEnd={p_.onDragEnd}
                    >
                        <div ref={blockRef} className={cn(styles.content, styles[`${p_.item.status}Group`])}>
                            {p_.multipleSelect && (
                                <div
                                    className={cn(styles.checkWrapper, styles.checkWrapperColExtra)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={p_.item.__view.selected ? { opacity: 1 } : {}}
                                >
                                    <Checkbox
                                        mod={'circleDis'}
                                        onClick={(selected: boolean) => {
                                            p_.onSelect({ selected, item: p_.item })
                                        }}
                                        checked={p_.item.__view.selected}
                                        className={'test-device-chbx'}
                                    />
                                </div>
                            )}
                            <div
                                className={cn(grid.full, { [styles.blackLayer]: active || isLightweight })}
                                style={{ zoom: zoomValue() }}
                            >
                                <div
                                    className={cn(
                                        textStyles.center,
                                        textStyles.white,
                                        minizedTileView ? '' : styles.displayTextGroup
                                    )}
                                >
                                    {!isLightweight &&
                                        helpers.isAvailable({ key: 'broadcasts', action: 'read' }) &&
                                        !p_.item.hasBroadcast && <div>{translate('noBroadcast')}</div>}
                                    {tooltipRef.current ? (
                                        <div className={cn(item.ellipsis, styles.titleStyles)}>{name}</div>
                                    ) : (
                                        <DotTooltip tooltip={{ html: name }}>
                                            <div className={cn(item.ellipsis, styles.titleStyles)}>{name}</div>
                                        </DotTooltip>
                                    )}
                                </div>
                                <div className={cn(getStatusPanelStyle(), styles.statusPanel)}>
                                    {renderGroupStatus()}
                                </div>
                            </div>
                        </div>
                    </DndDraggable>
                    <div className={styles.groupBackWrapper}>
                        <div
                            className={`${styles.groupBack} ${styles.groupBack_second} ${
                                styles[`${p_.item.status}Group`]
                            }`}
                        />
                    </div>
                    <div className={styles.groupBackWrapper}>
                        <div className={`${styles.groupBack} ${styles.groupBack_first}`} />
                    </div>
                </Rectangle>
            )}
        </div>
    )
}

export default ItemDisplays
